// The free booking page generator (/tools/booking-page-generator, LIST #75).
//
// The visitor's answers become a small "booking draft". Nothing is saved on
// this site or sent anywhere: the draft drives the real 369.dev booking page
// preview (bookingPreviewPayload) and rides to sign-up in the trial link
// (bookingSignupUrl). After the card step the app opens its "Your booking
// page" setup step filled in from it, and one tap publishes the page.
//
// Wire format (KEEP IN SYNC with the app's
// lib/view/account_setup/booking_draft.dart, which re-validates every field):
//   booking_draft = base64url(UTF-8 JSON), no padding, of
//   { v: 1,
//     name: string (1–80),                       business name
//     services: [{ name: string (1–80),          1 to 3 services
//                  minutes: 30 | 60 | 90,
//                  price: number (0–100000, 2 decimals) }],
//     meet: 'video' | 'phone' | 'in_person',
//     hours: 'weekdays' | 'weekdays_saturday' }
// These are exactly the choices of the app's setup step, so the page the
// visitor previews is the page the step makes.

export const BOOKING_DRAFT_VERSION = 1;
export const BOOKING_DRAFT_LIMITS = Object.freeze({ name: 80, services: 3, price: 100000 });
export const BOOKING_DRAFT_LENGTHS = Object.freeze([30, 60, 90]);
export const BOOKING_DRAFT_MEETS = Object.freeze(["video", "phone", "in_person"]);
export const BOOKING_DRAFT_HOURS = Object.freeze(["weekdays", "weekdays_saturday"]);

/** The sign-up attribution tag (recorded by the app's recordSignupAcquisition). */
export const BOOKING_GENERATOR_SOURCE = "booking-generator";
export const BOOKING_PREVIEW_ORIGIN = "https://369.dev";
export const BOOKING_PREVIEW_PATH = "/booking/preview";

const SIGNUP_URL = "https://app.workspace369.com/signup";

function cleanText(value, max) {
  if (typeof value !== "string") return "";
  // Control and invisible format characters (bidi overrides, zero-width
  // marks) out, whitespace runs collapsed, then cut by code point. The app
  // refuses a draft that still has any.
  const flat = value.replace(/[\u00ad\u200c\u200d]/g, "").replace(/[\p{Cc}\p{Cf}]/gu, " ").replace(/\s+/g, " ").trim();
  return Array.from(flat).slice(0, max).join("").trim();
}

function cleanPrice(value) {
  const number = typeof value === "number" ? value : Number.parseFloat(String(value ?? "").replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(number) || number <= 0) return 0;
  return Math.round(Math.min(number, BOOKING_DRAFT_LIMITS.price) * 100) / 100;
}

/**
 * The draft for the form's answers, or null when there is nothing to make a
 * page from (no business name or no named service).
 */
export function buildBookingDraft(form) {
  const name = cleanText(form?.businessName, BOOKING_DRAFT_LIMITS.name);
  const services = (Array.isArray(form?.services) ? form.services : [])
    .map((service) => ({
      name: cleanText(service?.name, BOOKING_DRAFT_LIMITS.name),
      minutes: BOOKING_DRAFT_LENGTHS.includes(Number(service?.minutes)) ? Number(service.minutes) : 30,
      price: cleanPrice(service?.price),
    }))
    .filter((service) => service.name)
    .slice(0, BOOKING_DRAFT_LIMITS.services);
  if (!name || services.length === 0) return null;
  return {
    v: BOOKING_DRAFT_VERSION,
    name,
    services,
    meet: BOOKING_DRAFT_MEETS.includes(form?.meet) ? form.meet : "video",
    hours: BOOKING_DRAFT_HOURS.includes(form?.hours) ? form.hours : "weekdays",
  };
}

function base64UrlFromBytes(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const base64 = typeof btoa === "function" ? btoa(binary) : Buffer.from(binary, "binary").toString("base64");
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** base64url (no padding) of the draft's UTF-8 JSON. */
export function encodeBookingDraft(draft) {
  return base64UrlFromBytes(new TextEncoder().encode(JSON.stringify(draft)));
}

/** Reverses encodeBookingDraft (tests and debugging); null when it doesn't parse. */
export function decodeBookingDraft(token) {
  try {
    const base64 = String(token).replace(/-/g, "+").replace(/_/g, "/");
    const binary = typeof atob === "function" ? atob(base64) : Buffer.from(base64, "base64").toString("binary");
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/**
 * The trial link: Specialist (booking pages start there, and the live page
 * refuses to show below it), the attribution tag, and the draft. Without a
 * draft it is the plain Specialist sign-up.
 */
export function bookingSignupUrl(draft) {
  const url = new URL(SIGNUP_URL);
  url.searchParams.set("plan", "specialist");
  url.searchParams.set("w369_src", BOOKING_GENERATOR_SOURCE);
  if (draft) url.searchParams.set("booking_draft", encodeBookingDraft(draft));
  return url.toString();
}

const LOCATION_TYPES = { video: "custom", phone: "phone", in_person: "in_person" };

/** What a client reads under the location: the app's own words (app_en.arb onboardBooking*Details). */
export const MEET_DETAILS = Object.freeze({
  video: "Video call. I'll send you the link before we meet.",
  phone: "Phone call. I'll call you on the number you give.",
  in_person: "",
});

/** The line under the page title: the app's onboardBookingPageDescription. */
export const PAGE_DESCRIPTION = "Pick a service and a time that works for you.";

/**
 * The 369.dev booking preview's render payload (its BookingPreviewPayload)
 * for a draft. The preview fills anything not sent from its sample business
 * (Northwind Studio), so the sample's industry, website, email and team are
 * cleared here: a new trial's page has the owner as its one host. The
 * calendar stays the sample's (weekdays, 9:00 to 16:30); the live page uses
 * the draft's own days. `timeZone` (an IANA name, the visitor's) labels it.
 */
export function bookingPreviewPayload(draft, { step = "service", timeZone = "" } = {}) {
  if (!draft) return null;
  // A page the app's setup step makes: one host assigned automatically on a
  // single scrolling page (BookingPageModel's defaults), so no host step.
  const page = {
    name: draft.name,
    title: draft.name,
    description: PAGE_DESCRIPTION,
    host_selection_mode: "auto_assign",
    flow_style: "single_page",
  };
  if (/^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+){0,2}$/.test(timeZone)) page.display_timezone = timeZone;
  return {
    v: 1,
    step,
    data: {
      workspace: { business_name: draft.name, industry: "", business_website: "", business_email: "", image_url: "" },
      page,
      hosts: [{ id: "generator-owner", name: draft.name }],
      services: draft.services.map((service, index) => ({
        id: `generator-service-${index + 1}`,
        name: service.name,
        price: service.price,
        price_type: "fixed",
        duration_minutes: service.minutes,
        currency: "usd",
        booking: {
          duration_minutes: service.minutes,
          location_type: LOCATION_TYPES[draft.meet] ?? "custom",
          location_details: MEET_DETAILS[draft.meet] ?? "",
        },
      })),
    },
  };
}

/** The preview iframe's address for a host page at `origin` on `channel`. */
export function bookingPreviewSrc(origin, channel) {
  const url = new URL(BOOKING_PREVIEW_PATH, BOOKING_PREVIEW_ORIGIN);
  url.searchParams.set("host", origin);
  url.searchParams.set("channel", channel);
  url.searchParams.set("embed", "1");
  return url.toString();
}
